import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation
} from "@angular/core";
import { AwsImage, CmsData } from "@dotcom-frontend/data";
import {
  LgAccordionComponent,
  LgAccordionItemComponent,
  LgAccordionPanelHeadingComponent,
  LgGridColDirective,
  LgGridRowDirective,
  LgIconComponent
} from "@legal-and-general/canopy";
import { Store } from "@ngrx/store";
import { Observable } from "rxjs";
import { WysiwygDirective } from "../../../directives/wysiwyg.directive";
import { AnalyticsService } from "../../../services/analytics/analytics.service";
import { ComponentLoaderService } from "../../../services/component-loader/component-loader.service";
import { HtmlTagService } from "../../../services/html-tag/html-tag.service";
import { setYoutubeScriptLoaded } from "../../../store/global/global.actions";
import { GlobalState } from "../../../store/global/global.reducer";
import {
  selectPageTitle,
  selectPageType,
  selectYoutubeScriptLoaded
} from "../../../store/global/global.selectors";
import { ImageComponent } from "../../globals/Image/Image.component";
import { DynamicComponentItem } from "../../utils/dynamic-loader/dynamic-component-item";

import { LoaderComponent } from "../../utils/dynamic-loader/loader.component";
import { generateRandomId } from "../../utils/utils";
import { DynamicComponent } from "../../utils/dynamic-loader/dynamic.component";
import { SafeHtmlPipe } from "../../utils/pipes/safe-html..pipe";
import {
  DatePipe,
  DecimalPipe,
  NgClass,
  NgTemplateOutlet
} from "@angular/common";
import { DomSanitizer } from "@angular/platform-browser";

@Component({
  selector: "dotcom-frontend-video-block",
  templateUrl: "./VideoBlock.component.html",
  encapsulation: ViewEncapsulation.None,
  styleUrl: "./VideoBlock.component.scss",
  standalone: true,
  imports: [
    LgGridRowDirective,
    LgGridColDirective,
    ImageComponent,
    LoaderComponent,
    WysiwygDirective,
    LgIconComponent,
    LgAccordionComponent,
    SafeHtmlPipe,
    LgAccordionItemComponent,
    LgAccordionPanelHeadingComponent,
    NgTemplateOutlet,
    DatePipe,
    DecimalPipe,
    NgClass
  ]
})
export class VideoBlockComponent
  implements DynamicComponent, OnInit, OnDestroy
{
  @Input() data: CmsData | undefined;
  pageTitle = "";
  pageTitle$: Observable<string> = this.store.select(selectPageTitle);
  pageType$: Observable<string> = this.store.select(selectPageType);
  pageType: string | undefined;

  youtubeScriptLoaded$: Observable<boolean> = this.store.select(
    selectYoutubeScriptLoaded
  );
  youtubeScriptLoaded = false;

  modalComponent: DynamicComponentItem | undefined;
  headingComponent: DynamicComponentItem | undefined;
  isInGridParent = false;
  bodyText: string | undefined;
  videoProvider!: string;
  videoId!: string;
  channelId!: string;
  ctaButtons: any;
  isLinkList = false;
  caption!: string;
  transcript: string | undefined;
  durationMinutes!: number;
  durationSeconds!: number;
  publishDate: any;
  thumbnailImage!: AwsImage;
  parent!: string;
  blockId!: string;
  videoName!: string;
  videoSize = 8;
  textSize = 8;
  loadVideo = false;
  autoPlay = false;
  hideDetails = false;
  playInModal = false;
  useAspectRatio = true;
  useVideoSize = true;
  showDescription = 0;
  photoId = "";
  showLogo = 0;
  hideBigPlay = 0;
  socialSharing = 0;
  subtitles = 0;
  autoMute = 0;
  twentyThreeAutoPlay = 0;
  loop = 0;
  ambient = 0;
  comingSoon = 0;
  startPoint!: number;
  quality: string | undefined;
  whenVideoEnds: string | undefined;
  twentyThreeVideoSrcParams: any;
  src: any;
  twentyThreeVideosrc = "https://am-videos.landg.com";
  displayAsBoxInGrid = false;
  registrationRequired = false;
  scriptId: string | undefined;

  constructor(
    public store: Store<{ global: GlobalState }>,
    private analyticsService: AnalyticsService,
    private componentLoaderService: ComponentLoaderService,
    private htmlTagService: HtmlTagService,
    private domSanitizer: DomSanitizer
  ) {
    this.pageTitle$.subscribe((title) => {
      this.pageTitle = title;
    });
    this.youtubeScriptLoaded$.subscribe((youtubeScriptLoaded) => {
      this.youtubeScriptLoaded = youtubeScriptLoaded;
    });
    this.pageType$.subscribe((pageType) => {
      this.pageType = pageType;
    });
  }

  async ngOnInit() {
    if (this.data) {
      if (this.data["headingBlock"]) {
        this.headingComponent = await this.componentLoaderService.loadComponent(
          "Heading",
          "globals",
          {
            headingBlock: this.data["headingBlock"]
          }
        );
      }

      if (
        this.data["parent"] == "box" ||
        this.data["parent"] == "grid" ||
        this.data["parent"] == "gridWithBackground"
      ) {
        this.isInGridParent = true;
      }

      if (
        (this.data["parent"] == "grid" ||
          this.data["parent"] == "gridWithBackground") &&
        this.data["displayAsBoxInGrid"] === true
      ) {
        this.displayAsBoxInGrid = true;
      }

      if (this.data["ctaButtons"]) {
        if (this.data["isLinkList"] && this.isInGridParent) {
          this.isLinkList = true;
          this.ctaButtons = await this.componentLoaderService.loadComponent(
            "LinkList",
            "globals",
            {
              interactionContainer: "Video Block",
              ctaButtons: this.data["ctaButtons"]
            }
          );
        } else {
          this.ctaButtons = [];
          for (const button of this.data["ctaButtons"].slice(0, 2)) {
            const buttonComponent =
              await this.componentLoaderService.loadComponent(
                "Button",
                "globals",
                {
                  href: button.href,
                  buttonStyle: button.buttonStyle,
                  text: button.text,
                  icon: button.icon,
                  target: button.target,
                  adobeData: {
                    interactionContainer: "Video Block",
                    interactionLink: button["href"],
                    interactionName: button["text"],
                    interactionPage: this.pageTitle,
                    interactionRegion: "",
                    interactionType: "ButtonClick",
                    interactionDetails: ""
                  }
                }
              );
            this.ctaButtons.push(buttonComponent);
          }
        }
      }

      this.registrationRequired = this.data["registrationRequired"];
      this.parent = this.data["parent"];
      if (this.registrationRequired) {
        if (this.parent !== "modal") {
          this.playInModal = true;
        } else {
          this.playInModal = false;
        }
      } else {
        this.playInModal = this.data["playInModal"] && this.parent !== "modal";
      }

      if (this.parent && ["grid", "box", "column"].includes(this.parent)) {
        this.hideDetails = true;
      }

      if (this.parent && ["grid", "box"].includes(this.parent)) {
        this.useAspectRatio = false;
      }

      if (
        this.parent &&
        ["grid", "box", "column", "narrow", "modal"].includes(this.parent)
      ) {
        this.useVideoSize = false;
      }

      if (
        this.parent === "narrow" ||
        this.pageType === "ArticlePage" ||
        this.displayAsBoxInGrid
      ) {
        this.textSize = 12;
      }

      if (this.parent !== "modal") {
        this.thumbnailImage = this.data["thumbnailImage"];
      }

      if (this.parent === "modal") {
        this.autoPlay = true;
      }

      this.bodyText = this.data["bodyText"];
      this.videoProvider = this.data["videoProvider"];
      this.videoId = this.data["videoId"];
      this.blockId = this.data["contentLink"]["id"] + generateRandomId(20);
      this.channelId = this.data["channelId"];
      this.caption = this.data["caption"];
      this.transcript = this.data["transcript"];
      this.durationMinutes = this.data["durationMinutes"];
      this.durationSeconds = this.data["durationSeconds"];
      this.publishDate = this.data["publishDate"];
      this.videoSize =
        this.data["videoSize"] === "smaller" &&
        this.useVideoSize &&
        this.pageType !== "ArticlePage"
          ? 8
          : 12;
      this.videoName = this.data["name"];

      if (this.isInGridParent) {
        this.videoSize = 12;
      }

      if (this.videoProvider === "twentythree") {
        this.photoId = this.data["photoId"];
        this.showDescription = Number(this.data["showDescription"]) || 0;
        this.showLogo = Number(this.data["showLogo"]) || 0;
        this.hideBigPlay = Number(this.data["hideBigPlay"]) || 0;
        this.socialSharing = Number(this.data["socialSharing"]) || 0;
        this.subtitles = Number(this.data["subtitles"]) || 0;
        this.autoMute = Number(this.data["autoMute"]) || 0;
        this.twentyThreeAutoPlay = Number(this.data["autoPlay"]) || 0;
        this.loop = Number(this.data["loop"]) || 0;
        this.ambient = Number(this.data["ambient"]) || 0;
        this.comingSoon = Number(this.data["comingSoon"]) || 0;
        this.startPoint = this.data["startPoint"] || "";
        this.quality = this.data["defaultQuality"] || "";
        this.whenVideoEnds = this.data["whenVideoEnds"] || "";

        if (
          this.data["twentyThreeAccount"] &&
          this.data["twentyThreeAccount"][0]["url"]
        ) {
          this.twentyThreeVideosrc = this.data["twentyThreeAccount"][0]["url"];
        }

        this.twentyThreeVideoSrcParams =
          this.twentyThreeVideosrc +
          "/v.ihtml/player.html?source=embed" +
          "&token=" +
          this.videoId +
          "&photo_id=" +
          this.photoId +
          "&showDescription=" +
          this.showDescription +
          "&showLogo=" +
          this.showLogo +
          "&hideBigPlay=" +
          this.hideBigPlay +
          "&socialSharing=" +
          this.socialSharing +
          "&subtitlesOnByDefault=" +
          this.subtitles +
          "&autoPlay=" +
          this.twentyThreeAutoPlay +
          "&autoMute=" +
          this.autoMute +
          "&loop=" +
          this.loop +
          "&ambient=" +
          this.ambient +
          "&start=" +
          this.startPoint +
          "&defaultQuality=" +
          this.quality +
          "&whenVideoEnds=" +
          this.whenVideoEnds;
        this.src = this.domSanitizer.bypassSecurityTrustResourceUrl(
          this.twentyThreeVideoSrcParams
        );
      }

      if (this.videoProvider === "twentythreespot") {
        this.scriptId = this.data["scriptId"];
        this.htmlTagService.addTag({
          tag: "script",
          identifier: `js-twentyThree-video-script-${this.videoId}-${this.scriptId}`,
          type: "text/javascript",
          src: `https://videos.lgim.com/spot/${this.videoId}/${this.scriptId}/include.js`,
          position: "body",
          async: "true"
        });
      }

      if (!this.thumbnailImage) {
        this.loadVideo = true;
        this.addVideoProviderScript();
      }

      if ((this.hideDetails && this.playInModal) || this.playInModal) {
        this.modalComponent = await this.componentLoaderService.loadComponent(
          "Modal",
          "globals",
          {
            modalData: this.data
          }
        );
      }

      if (this.data["enableVideoSchema"]) {
        this.addSchemaJson();
      }
    }
  }

  playVideo(event: any) {
    if (event.key && event.key !== "Enter") {
      return;
    }
    this.autoPlay = true;
    this.loadVideo = true;
    this.addVideoProviderScript();
    this.recordVideoAnalytics();
  }

  addVideoProviderScript() {
    if (this.videoProvider === "brighttalk") {
      if (this.registrationRequired) {
        setTimeout(() => {
          this.htmlTagService.addTag({
            tag: "script",
            identifier: `jsBrightTALKEmbedConfig`,
            type: "application/json",
            position: `jsBrightTALKEmbedContainer-${this.blockId}`,
            text: `{ "channelId" : ${this.channelId}, "language": "en-US", "commId" : ${this.videoId}, "displayMode" : "standalone", "height" : "auto" }`
          });
        }, 100);
        setTimeout(() => {
          this.htmlTagService.addTag({
            tag: "script",
            identifier: `jsBrightTALKEmbed jsBrightTALKEmbed-${this.blockId}`,
            type: "text/javascript",
            src: "https://www.brighttalk.com/clients/js/player-embed/player-embed.js",
            position: `jsBrightTALKEmbedContainer-${this.blockId}`
          });
        }, 100);
      } else {
        setTimeout(() => {
          this.htmlTagService.addTag({
            tag: "script",
            identifier: `jsBrightTALKEmbed-${this.blockId}`,
            type: "text/javascript",
            src: "https://www.brighttalk.com/clients/js/embed/embed.js",
            position: `jsBrightTALKEmbedContainer-${this.blockId}`
          });
        }, 100);
      }
    } else if (this.videoProvider === "twentythree") {
      setTimeout(() => {
        this.htmlTagService.addTag({
          tag: "script",
          identifier: `js-twentyThree-video-script`,
          type: "text/javascript",
          src: this.twentyThreeVideosrc,
          position: "body"
        });
      }, 100);
    } else if (this.videoProvider === "youtube") {
      if (!this.youtubeScriptLoaded) {
        this.htmlTagService.addTag({
          tag: "script",
          identifier: `js-youtube-video-script`,
          type: "text/javascript",
          src: "https://www.youtube.com/iframe_api",
          position: "body"
        });

        this.store.dispatch(
          setYoutubeScriptLoaded({ youtubeScriptLoaded: true })
        );
        const wind = window as any;
        wind["onYouTubeIframeAPIReady"] = () => {
          this.initYoutubeVideo();
        };
      } else {
        setTimeout(() => {
          this.initYoutubeVideo();
        }, 0);
      }
    }
  }

  initYoutubeVideo() {
    const wind = window as any;
    new wind["YT"].Player(`js-youtube-video${this.blockId}`, {
      videoId: this.videoId,
      playerVars: { autoplay: 1 }
    });
  }

  recordVideoAnalytics() {
    this.analyticsService.adobeInteractionEvent(
      "Video Block",
      this.videoId,
      this.videoName,
      "",
      "VideoPlay",
      this.pageTitle,
      this.videoProvider
    );
  }

  recordTranscriptAnalytics(action: string) {
    this.analyticsService.adobeInteractionEvent(
      "Video Block",
      action,
      "Transcript",
      "",
      "ItemClick",
      this.pageTitle,
      ""
    );
  }

  addSchemaJson() {
    if (this.data) {
      const schemaJson = {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: this.data["schemaVideoName"],
        description: this.data["schemaVideoDescription"],
        thumbnailUrl: this.data["schemaThumbnailImage"]?.url,
        uploadDate: this.data["schemaUploadDate"],
        duration: `PT${this.data["durationMinutes"]}M`,
        contentUrl:
          this.data["schemaContentUrl"] &&
          this.data["schemaContentUrl"][0]?.href,
        embedUrl:
          this.data["schemaEmbeddedUrl"] &&
          this.data["schemaEmbeddedUrl"][0]?.href,
        publication: this.data["schemaPublication"],
        expiryDate: this.data["schemaExpiryDate"]
      };

      this.htmlTagService.addTag({
        tag: "script",
        identifier: `js-schema-video-block-${this.data["contentLink"]["id"]}`,
        text: JSON.stringify(schemaJson),
        type: "application/ld+json"
      });
    }
  }

  ngOnDestroy() {
    if (this.data && this.data["enableVideoSchema"]) {
      this.htmlTagService.addTag({
        tag: "script",
        identifier: `js-schema-video-block-${this.data["contentLink"]["id"]}`,
        mode: "remove"
      });
    }
  }
}
